import { CSSProperties, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { GenericSvg, SVGName } from "../../../assets/svgs/GenericSVG";
import { Body, Mini } from "../../styles/stringStyles";
import { useStyles } from "../../styles/theme";
import { hexWithOpacity } from "../../utils/hexWithOpacity";
import { CustomButton } from "../buttons/CustomButton";
import { IconButton } from "../buttons/IconButton";
import { SkeletonPlaceholder } from "../loading/SkeletonPlaceholder";

type Option = {
  value: string | undefined;
  label: string;
};

type Props = {
  error?: string;
  value: string;
  options: Option[];
  onChange?: (label: string, value: string | undefined) => void;
  placeholder?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  style?: CSSProperties;
  disabled?: boolean;
  id?: string;
  label?: string;
  loadingData?: boolean;
  withoutError?: boolean;
  icon?: SVGName;
  className?: string;
  tag?: string;
  hideDropdownIcon?: boolean;
};

export const DropdownSelect = (props: Props) => {
  const theme = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Handle clicking outside of dropdown
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        props.onBlur?.();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [props]);

  const handleOptionClick = (option: Option) => {
    if (props.disabled) return;

    props.onChange?.(option.label, option.value);
    setIsOpen(false);
    props.onBlur?.();
  };

  const handleDropdownClick = () => {
    if (props.disabled) return;

    if (!isOpen) {
      props.onFocus?.();
    }
    setIsOpen(!isOpen);
  };

  return (
    <Container style={props.style} className={props.className}>
      <LabelContainer>
        {props.label && <StyledMini>{props.label}</StyledMini>}
        {props.tag && <TagValue>{props.tag}</TagValue>}
      </LabelContainer>
      {props.loadingData ? (
        <SkeletonWrapper />
      ) : (
        <DropdownContainer
          ref={dropdownRef}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls="dropdown-list"
          tabIndex={props.disabled ? -1 : 0}
        >
          <DropdownHeader
            onClick={handleDropdownClick}
            $isOpen={isOpen}
            $disabled={props.disabled}
            $hasIcon={!!props.icon}
          >
            {props.icon && (
              <LeftIconWrapper>
                <IconButton
                  iconName={props.icon}
                  size={24}
                  primaryColor={theme.colors.medium}
                />
              </LeftIconWrapper>
            )}
            <SelectedText $isEmpty={!props.value}>
              {(props.value || props.placeholder) ?? " "}
            </SelectedText>
            {!props.hideDropdownIcon && (
              <IconButtonWrapper>
                <GenericSvg
                  name={isOpen ? "ArrowUp" : "ArrowDown"}
                  size={24}
                  primaryColor={theme.colors.medium}
                />
              </IconButtonWrapper>
            )}
          </DropdownHeader>
          {isOpen && (
            <DropdownList id="dropdown-list" role="listbox">
              {props.options.map((option, index) => (
                <DropdownItem
                  key={option.value + " " + index}
                  role="option"
                  aria-selected={option.value === props.value}
                  onClick={() => handleOptionClick(option)}
                >
                  <Body>{option.label}</Body>
                </DropdownItem>
              ))}
            </DropdownList>
          )}
        </DropdownContainer>
      )}
      {!props.withoutError && (
        <Error>
          <ErrorText>{props.error ?? " "}</ErrorText>
        </Error>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownHeader = styled(CustomButton)<{
  $isOpen: boolean;
  $disabled?: boolean;
  $hasIcon: boolean;
}>`
  width: 100%;
  border-radius: 10px;
  border: 1.5px solid
    ${({ theme, $isOpen }) =>
      $isOpen
        ? theme.colors.primary
        : hexWithOpacity(theme.colors.medium, 0.25)};
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.dark};
  padding: 8px 12px;
  font: 400 16px/26px Ubuntu, sans-serif;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};

  ${(props) =>
    props.$hasIcon &&
    `
    padding-left: 44px;
  `}
`;

const SelectedText = styled.div<{ $isEmpty: boolean }>`
  color: ${({ theme, $isEmpty }) =>
    $isEmpty ? hexWithOpacity(theme.colors.dark, 0.5) : theme.colors.dark};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DropdownList = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 10px;
  border: 1.5px solid ${({ theme }) => theme.colors.light};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 9;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  width: max-content;
`;

const DropdownItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) =>
      hexWithOpacity(theme.colors.primary, 0.1)};
  }
`;

const IconButtonWrapper = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`;

const LeftIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`;

const StyledMini = styled(Body)`
  color: ${({ theme }) => hexWithOpacity(theme.colors.dark, 0.6)};
`;

const SkeletonWrapper = styled(SkeletonPlaceholder)`
  width: 100%;
  height: 42px;
`;

const ErrorText = styled(Mini)`
  color: ${({ theme }) => theme.colors.lightRed};
`;

const Error = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding-left: 5px;
  padding-right: 10px;
  max-lines: 1;
  height: 20px;
`;

const LabelContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  padding-bottom: 5px;
`;

const TagValue = styled(Mini)`
  background-color: ${({ theme }) => hexWithOpacity(theme.colors.medium, 0.1)};
  color: ${({ theme }) => hexWithOpacity(theme.colors.dark, 0.6)};
  border-radius: 5px;
  padding: 2px 6px;
`;
